import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Lock, Shield, Eye, EyeOff } from "lucide-react";

export default function AdminConsoleLogin() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => { document.title = "OxyCom · Vault"; }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const r = await api.post("/admin/console/login", { email: email.trim(), password });
      try { localStorage.setItem("oxy_token", r.data.session_token); } catch {}
      setUser(r.data.user);
      toast.success(`Welcome back, ${r.data.user.name}`);
      navigate("/admin", { replace: true });
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Login failed";
      setErr(msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 relative overflow-hidden p-4" data-testid="admin-vault">
      {/* Ambient grid */}
      <div className="absolute inset-0 opacity-20" style={{backgroundImage:"linear-gradient(rgba(56,189,248,.2) 1px,transparent 1px),linear-gradient(90deg,rgba(56,189,248,.2) 1px,transparent 1px)",backgroundSize:"40px 40px"}}/>
      <div className="absolute -top-32 -right-32 w-96 h-96 bg-sky-500/20 blur-3xl rounded-full"/>
      <div className="absolute -bottom-32 -left-32 w-96 h-96 bg-emerald-500/10 blur-3xl rounded-full"/>

      <div className="relative w-full max-w-md">
        <div className="text-center mb-6">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-sky-500 to-emerald-500 flex items-center justify-center mx-auto shadow-lg shadow-sky-500/30">
            <Shield className="w-8 h-8 text-white"/>
          </div>
          <h1 className="mt-4 text-3xl font-black text-white tracking-tight">OxyCom Vault</h1>
          <p className="text-slate-400 text-sm mt-1">Super Admin Console — Restricted Access</p>
        </div>

        <form onSubmit={submit} className="bg-slate-900/80 backdrop-blur border border-slate-800 rounded-2xl p-6 space-y-4 shadow-2xl">
          <div>
            <Label className="text-slate-300 text-xs uppercase tracking-wide">Email</Label>
            <Input value={email} onChange={e => setEmail(e.target.value)} placeholder="admin@..." className="bg-slate-800 border-slate-700 text-white mt-1 h-11" autoFocus autoComplete="email" data-testid="vault-email"/>
          </div>
          <div>
            <Label className="text-slate-300 text-xs uppercase tracking-wide">Password</Label>
            <div className="relative mt-1">
              <Input type={showPw ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} className="bg-slate-800 border-slate-700 text-white h-11 pr-10" autoComplete="current-password" data-testid="vault-password"/>
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white">
                {showPw ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
              </button>
            </div>
          </div>
          {err && <div className="p-3 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-300 text-sm" data-testid="vault-error">{err}</div>}
          <Button type="submit" disabled={busy} className="w-full h-11 bg-gradient-to-r from-sky-500 to-emerald-500 hover:opacity-90 text-white font-bold shadow-lg shadow-sky-500/30" data-testid="vault-submit">
            <Lock className="w-4 h-4 mr-2"/>{busy ? "Verifying…" : "Enter Vault"}
          </Button>
          <div className="text-center text-xs text-slate-500 pt-2 border-t border-slate-800">
            5 failed attempts → 15-minute lockout. All actions are logged.
          </div>
        </form>
      </div>
    </div>
  );
}
